import React, { useEffect } from 'react';
import '../css/animate.css';
import '../css/barvy.css';
import '../css/style.css';
import { motion } from "framer-motion";
import { NavLink, useParams } from 'react-router-dom';
import testbike from "../images/bikes/1.png";
import { useState } from 'react';
import SeznamKolaLoader from "../website/seznamkol-loader";
import { useMemo } from 'react';
import { getLocalizedText } from '../utils/textLocalization';
import { getCurrentLanguage } from '../utils/languageSwitcher';
import { getModelEntryData, useEditry } from '@lazylabs-dev/editry-react';
import { isEditryConfigured } from '../utils/editryConfig';

const LOCALE_TO_CONTENT_LANGUAGE = {
  'cs-CZ': 'cs',
  'sk-SK': 'sk',
  'pl-PL': 'pl',
  'hu-HU': 'hu',
};

const normalizeTag = (tag) => {
  if (typeof tag === 'string') {
    return tag.toUpperCase();
  }

  if (tag && typeof tag === 'object') {
    if (typeof tag.value === 'string') {
      return tag.value.toUpperCase();
    }
  }

  return '';
};

const parseModelItemData = (item) => {
  const parsed = getModelEntryData(item);
  if (parsed && Object.keys(parsed).length > 0) {
    return parsed;
  }

  if (item && typeof item.data_json === 'string') {
    try {
      return JSON.parse(item.data_json);
    } catch {
      return {};
    }
  }

  return {};
};

const normalizeString = (value) => String(value || '').toLowerCase().trim();

const extractCategoryReference = (entry) => {
  if (!entry) {
    return null;
  }

  if (typeof entry === 'object' && entry.kategora) {
    return entry.kategora;
  }

  return entry;
};

const matchesCategory = (categoryEntry, targetCategory) => {
  const ref = extractCategoryReference(categoryEntry);

  if (!ref || !targetCategory) {
    return false;
  }

  const targetIds = new Set([
    String(targetCategory.id || ''),
    String(targetCategory.data?.id || ''),
  ].filter(Boolean));

  const targetStrings = new Set([
    normalizeString(targetCategory.slug),
    normalizeString(targetCategory.data?.cesta),
    normalizeString(targetCategory.data?.nazev),
    normalizeString(targetCategory.name),
  ].filter(Boolean));

  if (typeof ref === 'string' || typeof ref === 'number') {
    return targetIds.has(String(ref)) || targetStrings.has(normalizeString(ref));
  }

  const refId = String(ref.id || ref.item_id || ref.value || '');
  if (refId && targetIds.has(refId)) {
    return true;
  }

  const refStrings = [
    normalizeString(ref.slug),
    normalizeString(ref.cesta),
    normalizeString(ref.nazev),
    normalizeString(ref.name),
  ].filter(Boolean);

  return refStrings.some((item) => targetStrings.has(item));
};


function App({ bikeData }) {
  const { client } = useEditry();
  const editryReady = isEditryConfigured();
  const language = LOCALE_TO_CONTENT_LANGUAGE[getCurrentLanguage()] || 'cs';

  // Get localized text
  const bikesText = getLocalizedText('general.bikes_caps');
  const allBikesText = getLocalizedText('general.all_bikes');
  const showButtonText = getLocalizedText('general.show_button');
  const pruvodceButtonText = getLocalizedText('general.pruvodce_button');
  const pruvodceText = getLocalizedText('general.pruvodce_text');

  const { kategorie } = useParams();
  const [kategorieDataS, setKategorieDataS] = useState(null);
  const [bikeDataCorrected, setBikeDataCorrected] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeTag, setActiveTag] = useState("Tag0");

  const [ikony, setIkony] = useState([]);
  const [modelBikeData, setModelBikeData] = useState([]);

  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    const fetchIkony = async () => {
      try {
        if (!editryReady) {
          setIkony([]);
          return;
        }

        const modelsResponse = await client.getPublicModels();
        const iconsModel = modelsResponse?.data?.find((model) => model.slug === 'ikony');

        if (!iconsModel) {
          setIkony([]);
          return;
        }

        const itemsResponse = await client.getPublicModelItems(iconsModel.id, language);
        const processedIkony = (itemsResponse?.data || []).map(item => {
          const fields = getModelEntryData(item);
          return {
          id: item.id,
          nazev: fields.nazev,
          ikona: fields.ikona
        };
        });
        setIkony(processedIkony);
      } catch (error) {
        console.error("Chyba při načítání ikon:", error);
      }
    }
    fetchIkony();
  }, [client, editryReady, language])

  useEffect(() => {
    const fetchKola = async () => {
      try {
        if (!editryReady) {
          setModelBikeData([]);
          return;
        }

        const modelsResponse = await client.getPublicModels();
        const bikesModel = modelsResponse?.data?.find((model) => model.slug === 'kola');

        if (!bikesModel) {
          setModelBikeData([]);
          return;
        }

        let itemsResponse = await client.getPublicModelItems(bikesModel.id, language);
        let modelItems = itemsResponse?.data || [];

        // Fallback for APIs where language filter is strict or unsupported.
        if (!modelItems.length) {
          itemsResponse = await client.getPublicModelItems(bikesModel.id);
          modelItems = itemsResponse?.data || [];
        }

        const processedBikes = modelItems.map((item) => ({
          id: item.id,
          slug: item.slug,
          name: item.name,
          data: parseModelItemData(item),
        }));

        setModelBikeData(processedBikes);
      } catch (error) {
        console.error("Chyba při načítání modelu kola:", error);
        setModelBikeData([]);
      }
    };

    fetchKola();
  }, [client, editryReady, language]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const normalizedKategorie = kategorie.toLowerCase().trim();

        if (!editryReady) {
          setKategorieDataS(null);
          return;
        }

        const modelsResponse = await client.getPublicModels();
        const categoriesModel = modelsResponse?.data?.find((model) => model.slug === 'pick-4');

        if (!categoriesModel) {
          setKategorieDataS(null);
          return;
        }

        const itemsResponse = await client.getPublicModelItems(categoriesModel.id, language);
        const categories = (itemsResponse?.data || []).map((item) => ({
          id: item.id,
          data: getModelEntryData(item),
          slug: item.slug,
          name: item.name,
        }));

        const matchingCategory = categories.find(cat => 
          cat.data && cat.data.cesta && 
          cat.data.cesta.toLowerCase().trim() === normalizedKategorie
        );
        
        if (matchingCategory) {
          document.title = "Nukeproof | " + matchingCategory.data.nazev.toUpperCase();
          setKategorieDataS(matchingCategory);
        } else {
          console.error("Kategorie nebyla nalezena");
          setKategorieDataS(null);
        }
      } catch (error) {
        console.error("Chyba při načítání dat:", error);
        setKategorieDataS(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [client, editryReady, kategorie, language]);

  const handleScroll = (e) => {
    const hash = e.currentTarget.hash.substring(1);
    setTimeout(() => {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  useEffect(() => {
    setBikeDataCorrected([]);
    const fetchBikes = async () => {
      if (!kategorieDataS || !kategorieDataS.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

          const data = Array.isArray(modelBikeData) && modelBikeData.length ? modelBikeData : bikeData;

          if (!Array.isArray(data) || !data.length) {
            setBikeDataCorrected([]);
            setLoading(false);
            return;
          }

          const filteredBikes = Array.isArray(data) ? data.filter(bike => {
            if (!bike.data || !bike.data.kategorie) {
              return false;
            }
            
            const kategorie = bike.data.kategorie;
            

            // If kategorie is an array (new format with kategora objects)
            if (Array.isArray(kategorie)) {
              return kategorie.some((kat) => matchesCategory(kat, kategorieDataS));
            }
            
            // If kategorie is a single object (old format) - fallback
            if (kategorie) {
              return matchesCategory(kategorie, kategorieDataS);
            }
            
            return false;
          }) : [];
        
          setBikeDataCorrected(filteredBikes);
          setLoading(false);
      } catch (error) {
        console.error("Chyba při načítání kol:", error);
        setLoading(false);
      }
    }

    fetchBikes();
  }, [kategorieDataS, bikeData, modelBikeData]);

  const setTag = (input) => {
    setActiveTag(input)
  }

    const allTags = useMemo(() => {
      const tags = bikeDataCorrected.flatMap(bike =>
          (bike.data?.tags || []).map(tag => normalizeTag(tag)).filter(Boolean)
      );
      return Array.from(new Set(tags));
    }, [bikeDataCorrected]);


    const filteredBikes = useMemo(() => {
      if (activeTag === "Tag0") return bikeDataCorrected;
        return bikeDataCorrected.filter(bike =>
        (bike.data?.tags || []).some(tag => normalizeTag(tag) === activeTag)
      );
    }, [bikeDataCorrected, activeTag]);

  const bikesElements = (() => {
    if (!Array.isArray(filteredBikes) || filteredBikes.length === 0) {
      return (
        <div className="no-bikes-message-shown">
          <p>V této kategorii nejsou žádná kola k dispozici.</p>
        </div>
      );
    }
    return filteredBikes.map((bike, index) => {
      const newIcon = ikony.find(i => String(i.nazev || '').toUpperCase() === "NEW" && bike.data?.ikony?.some(iconRef => {
        const iconName = typeof iconRef === 'string' ? iconRef : (iconRef?.value || '');
        return String(iconName || '').toUpperCase() === "NEW";
      }));
      return (
        <motion.div key={bike.id || index} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
          <NavLink key={index} to={"/kola/" + kategorieDataS.data.nazev.toLowerCase() + "/" + (bike.id || "example")}>
            <div className="pages-bikes" style={{ position: 'relative' }}>
              {newIcon && (
                <img
                  src={newIcon.ikona}
                  alt="NEW"
                  style={{ position: 'absolute', top: '10px', left: '10px', width: '45px', height: '45px', zIndex: 10, cursor: 'pointer' }}
                  onMouseEnter={(e) => {
                    const rect = e.target.getBoundingClientRect();
                    setTooltip({
                      text: newIcon.nazev,
                      x: rect.left + rect.width / 2,
                      y: rect.top - 20
                    });
                  }}
                  onMouseLeave={() => setTooltip(null)}
                />
              )}
              <div className="image-blocker">
                <img 
                  src={bike.data?.obrazek+"?format=webp" || testbike} 
                  alt={bike.data?.nazev || "Kolo"} 
                />
              </div>
              <h1>
                {bike.data?.nazev || "Neznámé kolo"}
                {bike.data?.ikony?.filter(iconRef => {
                  const iconName = typeof iconRef === 'string' ? iconRef : (iconRef?.value || '');
                  const icon = ikony.find(i => String(i.nazev || '').toUpperCase() === String(iconName || '').toUpperCase());
                  return icon && String(icon.nazev || '').toUpperCase() !== "NEW";
                }).map((iconRef, idx) => {
                  const iconName = typeof iconRef === 'string' ? iconRef : (iconRef?.value || '');
                  const icon = ikony.find(i => String(i.nazev || '').toUpperCase() === String(iconName || '').toUpperCase());
                  return icon ? (
                    <img
                      key={icon.id}
                      src={icon.ikona}
                      alt={icon.nazev}
                      style={{ width: '35px', height: '35px', marginLeft: '5px', marginBottom: '7px', verticalAlign: 'middle', cursor: 'pointer' }}
                      onMouseEnter={(e) => {
                        const rect = e.target.getBoundingClientRect();
                        setTooltip({
                          text: icon.nazev,
                          x: rect.left + rect.width / 2,
                          y: rect.top + -20
                        });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                    />
                  ) : null;
                })}
              </h1>
              <p>{bike.data?.oproduktu || bike.data?.oProduktu || "Popis není k dispozici"}</p>
              <button className="seznamkola-button button-left"><span>{showButtonText}</span></button>
                <div className="tags-on-bike">

                  {bike.data?.tags?.map((tag, i) => (
                    <h4 key={i}><span>{typeof tag === 'string' ? tag : (tag?.value || '')}</span></h4>
                  ))}

                </div>
            </div>
          </NavLink>
        </motion.div>
      );
    });
  })();

  return (
    <>
      {!loading && kategorieDataS && (
        <>
          {kategorieDataS.data.obrazek2 != null && ( 
            <div className="page-background-bike-list">
              <img src={kategorieDataS.data.obrazek2} alt="Nukeproof pozadí kola" />
              <div />
            </div>
          )}
        </>
      )}

      <motion.div className="page" key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
        {loading ? (
          <>
            <SeznamKolaLoader />
          </>
        ) : kategorieDataS && kategorieDataS.data ? (
          <>
          <div className="page-container page-container-bikes">
            <div className="page-boxes">

              <div className="cesta-seznam cesta-seznam-kol">
                <NavLink to="/#kola" onClick={handleScroll}><button><span>{bikesText}</span></button></NavLink>
                <button className="cesta-active"><span>{kategorieDataS.data.nazev.toUpperCase() || "loading"}</span></button>
              </div>

              <h1>{kategorieDataS.data.nazev || "Kategorie"}</h1>
              <h2 dangerouslySetInnerHTML={{ __html: kategorieDataS.data.popisKategorie }}></h2>

              <div className="tags-rework">
              <button onClick={() => setTag("Tag0")} className={activeTag === "Tag0" ? "tags-rework-active" : ""}>
                <span>{allBikesText}</span>
              </button>
              {allTags.map((tag, index) => (
                <button key={index} onClick={() => setTag(tag)} className={activeTag === tag ? "tags-rework-active" : ""}>
                  <span>{tag}</span>
                </button>
              ))}
              </div>

              { (kategorieDataS.data.nazev.toUpperCase() === "GRAVEL" || kategorieDataS.data.nazev.toUpperCase() === "ROAD") && (
              <div className="seznamkol-pruvodce">
                <p>{pruvodceText}</p>
                <NavLink to={"/pruvodce/" + kategorieDataS.data.nazev.toLowerCase()}><button><span>{pruvodceButtonText}</span></button></NavLink>
              </div>
              )}

              <div className="page-boxes-container">
                {bikesElements}
              </div>
            </div>
          </div>
          </>
        ) : (
          <>
            <SeznamKolaLoader />
          </>
        )}
      </motion.div>
      {tooltip && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '5px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            whiteSpace: 'nowrap',
            zIndex: 1000,
            transform: 'translateX(-50%)',
            pointerEvents: 'none'
          }}
        >
          {tooltip.text}
        </div>
      )}
    </>
  );
}

export default App;